import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '@/contexts/I18nContext';
import seoLanguages from '@/seo/languages.json';

const LANDING_PATHS = Object.fromEntries(seoLanguages.languages.map((l) => [l.code, l.path]));
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const LANGUAGE_LABELS = {
  en: 'English',
  hu: 'Magyar',
  de: 'Deutsch',
  es: 'Español',
  pt: 'Português',
  hi: 'हिन्दी',
  ur: 'اردو',
  pa: 'ਪੰਜਾਬੀ',
  ml: 'മലയാളം',
  te: 'తెలుగు',
  bg: 'Български',
  cs: 'Čeština',
  sk: 'Slovenčina',
  zh: '中文',
  yue: '廣東話',
};

export const LanguageSwitcher = ({ variant = 'ghost' }) => {
  const { language, setLanguage, availableLanguages } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onLanding = Object.values(LANDING_PATHS).includes(pathname);

  const choose = (code) => {
    setLanguage(code);
    if (onLanding && LANDING_PATHS[code]) navigate(LANDING_PATHS[code], { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size="sm" className="rounded-full gap-1.5 text-[#78716c] hover:text-[#1c1917]" data-testid="language-switcher">
          <Globe className="w-4 h-4" />
          <span className="text-sm font-medium">{LANGUAGE_LABELS[language] || language}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl">
        {availableLanguages.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => choose(code)}
            className={`text-base cursor-pointer ${language === code ? 'font-semibold text-[#b45309]' : ''}`}
            data-testid={`lang-option-${code}`}
          >
            {LANGUAGE_LABELS[code] || code}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
